<template>
  <v-app>
    <v-main class="main">

      <!-- Sidebar -->
      <AdminSidebar :drawerOpen="drawerOpen" :collapsed="isSidebarCollapsed" :isMobile="isMobile"
        @update:drawerOpen="drawerOpen = $event" />

      <v-container fluid class="main-content pa-4">

        <!-- Header -->
        <Header :isDarkMode="isDarkMode" :isMobile="isMobile" @toggle-sidebar="toggleSidebar" />

        <!-- Stats -->
        <v-row class="stats mt-4" dense>
          <v-col cols="12" sm="3">
            <StatCard title="Total Users" :value="totalUsersCount"
              icon="mdi-account-multiple-outline" iconColor="#22c55e"/>
          </v-col>
          <v-col cols="12" sm="3">
            <StatCard title="Pending KYC" :value="pendingKycCount" icon="mdi-account-lock-outline"
              iconColor="#ef4444"  />
          </v-col>
          <v-col cols="12" sm="3">
            <StatCard title="Compliant Users" :value="totalCompliantUsers" icon="mdi-account-check-outline"
              iconColor="#22c55e" />
          </v-col>
          <v-col cols="12" sm="3">
            <StatCard title="Inactive Users" :value="inactiveUsersCount" icon="mdi-account-multiple-remove-outline"
              iconColor="#ef4444" />
          </v-col>
        </v-row>

        <!-- Table Card -->
        <v-card class="table-card mt-6">

          <div class="table-header flex justify-between align-center flex-wrap">
            <!-- Left side: search + clear -->
            <div style="display: flex; align-items: center; gap: 16px; margin-bottom: 8px;">
              <InputText v-model="filters['global'].value" placeholder="Search Users..." />
              <Button type="button" icon="pi pi-filter-slash" label="Clear" variant="outlined" @click="clearFilters" />
            </div>
          </div>

          <!-- PrimeVue DataTable with search -->
          <DataTable v-model:filters="filters" :value="users" paginator :rows="10" dataKey="uuid" stripedRows
            class="modern-table" :rowsPerPageOptions="[5, 10, 20, 50]" tableStyle="min-width: 50rem"
            filterDisplay="menu" @row-click="goToDetails" selectionMode="single"
            :globalFilterFields="['profile.account_id', 'profile.firstname', 'profile.lastname', 'profile.username', 'status']">

            <!-- Empty table -->
            <template #empty>
              No Users found.
            </template>

            <!-- Loading state -->
            <template #loading>
              Loading Users...
            </template>

            <!-- Product Column -->
            <Column header="Name">
              <template #body="slotProps">
                <div class="product-cell">
                  <div>
                    <div class="product-name">{{ slotProps.data.profile.firstname }} {{ slotProps.data.profile.lastname }}</div>
                    <div class="product-desc">Account ID: {{ slotProps.data.profile.account_id }}</div>
                  </div>
                </div>
              </template>
            </Column>

            <!--User Column-->
            <Column header="Email">
              <template #body="slotProps">
                <div class="product-cell">
                  <div>
                    <div class="product-name">{{ slotProps.data.email }}</div>
                  </div>
                </div>
              </template>
            </Column>

            <!--Amount Column-->
            <Column header="Username">
              <template #body="slotProps">
                <div class="product-cell">
                  <div>
                    {{ slotProps.data.profile.username }}
                  </div>
                </div>
              </template>
            </Column>

             <!-- Status Column -->
            <Column header="Status">
              <template #body="slotProps">
                <span :class="[
                  'status-pill',
                  slotProps.data.account_status === 'active'
                    ? 'status-active'
                    : 'status-inactive'
                ]">
                  {{ slotProps.data.account_status === 'active' ? 'Active' : 'Inactive' }}
                </span>
              </template>
            </Column>


            <!--Type Column-->
            <Column header="Referral Code">
              <template #body="slotProps">
                <div class="product-cell">
                  <div>
                    {{ slotProps.data.referral_code }}
                  </div>
                </div>
              </template>
            </Column>

           
            <!--Source Column-->
            <!-- <Column header="Source">
              <template #body="slotProps">
                <div class="product-cell">
                  <div>
                    {{ slotProps.data.transaction_source }}
                  </div>
                </div>
              </template>
            </Column> -->

            <!-- Created Column -->
            <Column header="Account Created At">
              <template #body="slotProps">
                {{ formatDateTime(slotProps.data.created_at) }}
              </template>
            </Column>

          </DataTable>
        </v-card>
        <!-- Edit Modal -->
        <!-- <v-dialog v-model="editDialog" max-width="500px">
          <v-card>
            <v-card-title>Edit Product</v-card-title>
            <v-card-text>
              <v-text-field v-model="selectedProduct.product_name" label="Product Name" />
              <v-textarea v-model="selectedProduct.description" label="Description" />
              <v-switch v-model="selectedProduct.is_active" label="Active" />
            </v-card-text>
            <v-card-actions>
              <v-spacer></v-spacer>
              <v-btn text @click="closeEditModal">Cancel</v-btn>
              <v-btn color="primary" @click="saveEdit">Save</v-btn>
            </v-card-actions>
          </v-card>
        </v-dialog> -->
      </v-container>
    </v-main>
  </v-app>
</template>

<script setup>
import { ref, onMounted, onUnmounted, computed } from "vue"
import AdminSidebar from "@/components/AdminSidebar.vue"
import Header from "@/components/Header.vue"
import { userStore } from "@/store/user"
import StatCard from "@/components/StatCard.vue"
import { formatDateTime } from "@/util"
import { useRouter } from "vue-router"


const usr = userStore()
const router = useRouter()

const isSidebarCollapsed = ref(false)
const drawerOpen = ref(false)
const isMobile = ref(false)
const filters = ref({ global: { value: null, matchMode: "contains" } })

/* Mobile */
const toggleSidebar = () => {
  if (isMobile.value) drawerOpen.value = !drawerOpen.value
  else isSidebarCollapsed.value = !isSidebarCollapsed.value
}

const checkMobile = () => {
  isMobile.value = window.innerWidth < 768
  drawerOpen.value = !isMobile.value
}

/* Lifecycle */
onMounted(async () => {
  checkMobile()
  window.addEventListener("resize", checkMobile)
  try {
    await usr.loadUsers()
  } catch (error) {
    console.error(error)
  }
})

onUnmounted(() => {
  window.removeEventListener("resize", checkMobile)
})

const users = computed(() => usr.users?.data || [])

const totalUsersCount = computed(() => users.value.length)
const pendingKycCount = computed (() => usr.users?.pending_kyc || 0)
const totalCompliantUsers = computed(() => usr.users?.compliant_users || 0)
const inactiveUsersCount = computed(() => usr.users?.inactive_users || 0)


/* Clear filters */
const clearFilters = () => {
  filters.value = { global: { value: null, matchMode: "contains" } }
}

//clicking on the row
const goToDetails = (event) => {
  const row = event.data

  router.push({
    name: 'UserDetails', 
    params: { id: row.uuid }       
  })
}
</script>

<style scoped>
.main {
  display: flex;
  min-height: 100vh;
}

.main-content {
  flex: 1;
  background: #f8fafc;
}

/* Card */
.table-card {
  padding: 20px;
  border-radius: 14px;
  box-shadow: 0 6px 20px rgba(0, 0, 0, 0.05);
}

/* Product */
.product-cell {
  display: flex;
  align-items: center;
  gap: 12px;
}

.product-image {
  width: 40px;
  height: 40px;
  border-radius: 8px;
  object-fit: cover;
}

.product-name {
  font-weight: 700;
}

.product-desc {
  font-size: 16px;
  color: #64748b;
}

/* Status */
.status-pill {
  padding: 4px 10px;
  border-radius: 999px;
  font-size: 12px;
  font-weight: 600;
}

.status-active {
  background: #ecfdf5;
  color: #16a34a;
}

.status-inactive {
  background: #fef2f2;
  color: #dc2626;
}

/* Search input */
.search-wrapper {
  position: relative;
  display: flex;
  align-items: center;
}

.search-icon {
  position: absolute;
  left: 8px;
  color: #64748b;
}

.search-wrapper input {
  padding-left: 28px;
}

/* Button */
.theme-button {
  height: 46px;
  background: #000;
  color: #fff;
  border-radius: 10px;
  font-weight: 600;
  transition: all 0.25s ease;
}

/* Table */
.modern-table {
  border: none;
}

.table-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
}

:deep(.modern-table .p-datatable-thead > tr > th) {
  background: #eef7f0;
  color: #065f46;
  font-weight: 600;
  border-bottom: 2px solid #c1edc3;
}

.p-datatable-tbody > tr {
  cursor: pointer;
}

.p-datatable-tbody > tr:hover {
  background-color: #f5f5f5;
}
</style>
